import { Add } from '@carbon/react/icons';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { Button } from '@carbon/react';
import { DataTable } from 'components/data-table';
import ErrorToast from 'components/error-toast';
import { PageHeader } from 'components/page-header';
import { Pagination } from 'components/pagination';
import ValidityStatusIndicator from 'components/structure-grids/validity-status-indicator';
import moment from 'moment';
import { useAtom, useAtomValue } from 'jotai';
import { DEFAULT_PAGE_SIZE } from 'lib/ui';
import { capitalize, isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { StructureGridCollectionsQueryResponse } from 'types/api-responses';
import { VALIDITY_OPTIONS } from '../structure-grids-management/utils';
import { pageSizeAtom } from './atoms';
import { useFilterOptions } from './hooks';
import { StructureGridCollectionFilters } from './structure-grid-collection-filters';
import styles from './structure-grid-collection-management.module.scss';
import { getURLFilterParams } from './util';

// ─────────────────────────────────────
// Types
// ─────────────────────────────────────

type CollectionRow = StructureGridCollectionsQueryResponse &
  Record<string, unknown>;

interface StructureGridCollectionsQuerySearchParams extends SearchParams {
  basin?: string;
  owner?: string;
  createdAt?: {
    start?: string;
    end?: string;
  };
  sortBy?: string;
  sortOrder?: string;
  isDefault?: boolean;
  landingAutoUpdateEnabled?: boolean;
  validity?: string;
}

// ─────────────────────────────────────
// Column Definitions
// ─────────────────────────────────────

const columns = [
  {
    id: 'collectionName',
    accessorKey: 'collectionName',
    header: 'Structure Grid Collection',
    size: 240,
    cell: (info: {
      getValue: () => string;
      row: { original: CollectionRow };
    }) => (
      <Link to={`/structure_grid_collections/${info.row.original.id}`}>
        {info.getValue()}
      </Link>
    ),
  },
  {
    id: 'basin',
    accessorKey: 'basin',
    header: 'Basin',
    size: 120,
    meta: { type: 'enum' },
  },
  {
    id: 'isDefault',
    accessorKey: 'isDefault',
    header: 'Default',
    size: 90,
    meta: { type: 'boolean' },
    cell: (info: { getValue: () => boolean }) => {
      return info.getValue() ? 'Yes' : 'No';
    },
  },
  {
    id: 'intervals',
    accessorKey: 'intervals',
    header: 'Intervals in Collection',
    size: 160,
    meta: { type: 'integer' },
  },
  {
    id: 'owner',
    accessorKey: 'owner',
    header: 'Collection Owner',
    size: 180,
    meta: { type: 'enum' },
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Processed Status',
    size: 150,
    cell: (info: { getValue: () => string }) =>
      capitalize(String(info.getValue() ?? '')),
  },
  {
    id: 'validity',
    accessorKey: 'validity',
    header: 'Collection Validity',
    size: 170,
    meta: { type: 'enum' },
    cell: (info: { getValue: () => string }) => (
      <div className={styles.validityField}>
        <ValidityStatusIndicator status={String(info.getValue() ?? '')} />
        <span>{capitalize(String(info.getValue() ?? ''))}</span>
      </div>
    ),
  },
  {
    id: 'landingAutoUpdateEnabled',
    accessorKey: 'landingAutoUpdateEnabled',
    header: 'Auto Update Landings',
    size: 160,
    meta: { type: 'boolean' },
    cell: (info: { getValue: () => boolean }) => {
      return info.getValue() ? 'Yes' : 'No';
    },
  },
  {
    id: 'sourceCollectionCreatedAt',
    accessorKey: 'sourceCollectionCreatedAt',
    header: 'Created At',
    size: 130,
    meta: { type: 'date' },
    cell: (info: { getValue: () => string }) =>
      moment(info.getValue()).format('MM/DD/YYYY'),
  },
];

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

export default function StructureGridCollectionManagement() {
  const [pageSize, setPageSize] = useAtom(pageSizeAtom);
  const location = useLocation();
  const history = useHistory();

  const queryParams = new URLSearchParams(location.search);
  const {
    basin: basinQueryParam,
    createdStart: createdStartQueryParam,
    createdEnd: createdEndQueryParam,
    isDefault: isDefaultQueryParam,
    landingAutoUpdateEnabled: landingAutoUpdateEnabledQueryParam,
    owner: ownerQueryParam,
    status: statusQueryParam,
    validity: validityQueryParam,
    page: pageQueryParam,
    pageSize: pageSizeQueryParam,
  } = getURLFilterParams(queryParams);

  const { searchAtom: structureGridCollectionSearchAtom } = useMemo(
    () =>
      searchAtomPair<
        StructureGridCollectionsQueryResponse,
        StructureGridCollectionsQuerySearchParams
      >('structureGridCollections'),
    []
  );

  const [structureGridCollectionSearch, setStructureGridCollectionSearch] =
    useAtom(structureGridCollectionSearchAtom);

  const filterOptionsAtom = useFilterOptions();
  const filterOptions = useAtomValue(filterOptionsAtom);

  const defaultParams = {
    basin: 'all',
    date_filter_type: 'none',
    created_start: '',
    created_end: '',
    active_collection: 'all',
    owner: 'all',
    page: 1,
    page_size: pageSize,
  };

  const isLoading =
    structureGridCollectionSearch.loading || filterOptions.loading;

  const isError = structureGridCollectionSearch.error || filterOptions.error;

  const initialColumnFilters = useMemo((): ColumnFiltersState => {
    const filters: ColumnFiltersState = [];
    if (basinQueryParam && basinQueryParam !== 'all') {
      filters.push({ id: 'basin', value: [basinQueryParam] });
    }
    if (ownerQueryParam && ownerQueryParam !== 'all') {
      filters.push({ id: 'owner', value: [ownerQueryParam] });
    }
    if (validityQueryParam) {
      filters.push({ id: 'validity', value: [validityQueryParam] });
    }
    if (isDefaultQueryParam) {
      filters.push({ id: 'isDefault', value: [isDefaultQueryParam] });
    }
    if (landingAutoUpdateEnabledQueryParam) {
      filters.push({
        id: 'landingAutoUpdateEnabled',
        value: [landingAutoUpdateEnabledQueryParam],
      });
    }
    if (createdStartQueryParam || createdEndQueryParam) {
      filters.push({
        id: 'sourceCollectionCreatedAt',
        value: [createdStartQueryParam ?? null, createdEndQueryParam ?? null],
      });
    }
    return filters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Syncs TanStack ColumnFiltersState back to URL params so the existing
   * useEffect can pick them up and trigger the API call. This is the
   * same pattern as AFE catalog's onFilterChange → updateParams flow.
   */
  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      const updatedParams = new URLSearchParams(location.search);
      updatedParams.set('page', '1');

      // Reset all filter URL params to defaults
      updatedParams.set('basin', 'all');
      updatedParams.set('owner', 'all');
      updatedParams.delete('validity');
      updatedParams.delete('is_default');
      updatedParams.delete('landing_auto_update_enabled');
      updatedParams.set('created_start', '');
      updatedParams.set('created_end', '');
      updatedParams.set('date_filter_type', 'none');

      for (const filter of filters) {
        const value = filter.value;
        switch (filter.id) {
          case 'basin': {
            const v = (value as string[])[0];
            if (v) updatedParams.set('basin', v);
            break;
          }
          case 'owner': {
            const v = (value as string[])[0];
            if (v) updatedParams.set('owner', v);
            break;
          }
          case 'landingAutoUpdateEnabled': {
            const v = (value as string[])[0];
            if (v) updatedParams.set('landing_auto_update_enabled', v);
            break;
          }
          case 'isDefault': {
            const v = (value as string[])[0];
            if (v) updatedParams.set('is_default', v);
            break;
          }
          case 'validity': {
            const v = (value as string[])[0];
            if (v) updatedParams.set('validity', v);
            break;
          }
          case 'sourceCollectionCreatedAt': {
            const [start, end] = value as [string | null, string | null];
            if (start) updatedParams.set('created_start', start);
            if (end) updatedParams.set('created_end', end);
            if (start || end)
              updatedParams.set(
                'date_filter_type',
                'source_collection_created_at'
              );
            break;
          }
        }
      }

      history.push({
        pathname: location.pathname,
        search: updatedParams.toString(),
      });
    },
    [location, history]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      const updatedQueryParams = new URLSearchParams(location.search);
      updatedQueryParams.set('page', '1');
      history.push({
        pathname: location.pathname,
        search: updatedQueryParams.toString(),
      });

      if (sorting.length === 0) {
        setStructureGridCollectionSearch((current) => ({
          ...current,
          sortBy: 'sourceCollectionCreatedAt',
          sortOrder: 'desc',
          page: 1,
        }));
      } else {
        const { id, desc } = sorting[0];
        setStructureGridCollectionSearch((current) => ({
          ...current,
          sortBy: id,
          sortOrder: desc ? 'desc' : 'asc',
          page: 1,
        }));
      }
    },
    [location, history, setStructureGridCollectionSearch]
  );

  useEffect(
    () => {
      if (
        Array.from(queryParams.entries()).every(
          ([_key, value]) => isNil(value) || value === ''
        )
      ) {
        const updatedQueryParams = new URLSearchParams(location.search);
        Object.entries(defaultParams).forEach(([key, value]) => {
          updatedQueryParams.set(key, value.toString());
        });
        history.replace({
          pathname: location.pathname,
          search: updatedQueryParams.toString(),
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(
    () => {
      if (
        [basinQueryParam, createdStartQueryParam, createdEndQueryParam].every(
          (value) => isNil(value) || value === ''
        )
      ) {
        return;
      }
      const createdAtFilter =
        createdStartQueryParam || createdEndQueryParam
          ? {
              sourceCollectionCreatedAt: {
                start: createdStartQueryParam,
                end: createdEndQueryParam,
              },
            }
          : {};

      setStructureGridCollectionSearch(() => ({
        basin:
          basinQueryParam === defaultParams.basin ? undefined : basinQueryParam,
        ...createdAtFilter,
        sortBy: 'sourceCollectionCreatedAt',
        sortOrder: 'desc',
        page: Number(pageQueryParam),
        pageSize: Number(pageSizeQueryParam),
        isDefault: isDefaultQueryParam
          ? isDefaultQueryParam === 'true'
          : undefined,
        landingAutoUpdateEnabled: landingAutoUpdateEnabledQueryParam
          ? landingAutoUpdateEnabledQueryParam === 'true'
          : undefined,
        owner:
          ownerQueryParam === defaultParams.owner ? undefined : ownerQueryParam,
        status: statusQueryParam,
        validity: validityQueryParam,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      basinQueryParam,
      createdStartQueryParam,
      createdEndQueryParam,
      isDefaultQueryParam,
      landingAutoUpdateEnabledQueryParam,
      ownerQueryParam,
      statusQueryParam,
      validityQueryParam,
      pageQueryParam,
      pageSizeQueryParam,
    ]
  );

  const tableData = useMemo(
    () =>
      (structureGridCollectionSearch.data?.results ?? []) as CollectionRow[],
    [structureGridCollectionSearch.data?.results]
  );

  return (
    <>
      {isError && (
        <ErrorToast message="Error fetching structure grids collections" />
      )}
      <PageHeader
        title="Structure Grids Collections"
        breadcrumbs={[
          ['/structure_grids', 'Structure Grid Management'],
          ['/structure_grid_collections', 'Structure Grid Collections'],
        ]}
        actions={
          <div>
            <Button
              renderIcon={Add}
              onClick={() => history.push('/structure_grid_collections/new')}
            >
              Add Structure Grid Collection
            </Button>
          </div>
        }
      />
      <div className={styles.contentWrapper}>
        <div className={styles.tableWrapper}>
          <DataTable<CollectionRow>
            data={tableData}
            columns={columns as ColumnDef<CollectionRow>[]}
            getRowId={(row) => String(row.id)}
            persistenceKey="structure-grid-collections-table-v2"
            isLoading={isLoading}
            config={{ hideFooter: true, columnFilters: false }}
            initialTableState={{
              sortColumn: 'sourceCollectionCreatedAt',
              sortDirection: 'desc',
              columnFilters: initialColumnFilters,
            }}
            onSortChange={(s) => handleSortChange(s.sorting)}
            onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
            searchPlaceholder="Search collections..."
            renderFilterPanel={() => (
              <StructureGridCollectionFilters
                basins={filterOptions.basins}
                owners={filterOptions.owners}
                validityOptions={VALIDITY_OPTIONS}
              />
            )}
          />
        </div>
        <Pagination
          pageSizes={[16, 24, 48, DEFAULT_PAGE_SIZE]}
          pageSize={
            structureGridCollectionSearch.data?.pageSize || DEFAULT_PAGE_SIZE
          }
          page={structureGridCollectionSearch.data?.page || 1}
          totalItems={structureGridCollectionSearch.data?.totalCount || 0}
          onChange={({ page, pageSize: newPageSize }) => {
            setPageSize(newPageSize);
            const updatedQueryParams = new URLSearchParams(location.search);
            updatedQueryParams.set('page', page.toString());
            updatedQueryParams.set('page_size', newPageSize.toString());
            history.push({
              pathname: location.pathname,
              search: updatedQueryParams.toString(),
            });
          }}
        />
      </div>
    </>
  );
}
